import { ApiResult, HttpClient } from "../http.js";
import {
  VerificationCircuitMeasurementDto,
  VerificationCustomerInfoDto,
  VerificationDto,
  VerificationEquipmentDto,
  VerificationInstallationType,
  VerificationRcdMeasurementDto,
  VerificationStatus,
  VerificationTestingDto,
  VerificationVisualInspectionDto,
} from "../types/verifications.js";

/**
 * Body of a verification create/update.
 *
 * **Update is a FULL REPLACE** — the server writes every field as supplied, so a
 * field you omit is erased. Build the payload from the current {@link VerificationDto}
 * and override only what changed.
 *
 * `documentNumber` is absent on purpose: the server allocates it on create and
 * never lets a client change it.
 */
export interface VerificationPayload {
  version: number;
  documentDate: string;
  projectId?: string | null;
  projectTitle?: string | null;
  assignmentId?: string | null;
  assignmentTitle?: string | null;
  customer: VerificationCustomerInfoDto;
  installationType: VerificationInstallationType;
  installationAddress: string;
  visualInspection: VerificationVisualInspectionDto;
  testing: VerificationTestingDto;
  circuitMeasurements?: VerificationCircuitMeasurementDto[] | null;
  rcdMeasurements?: VerificationRcdMeasurementDto[] | null;
  equipment: VerificationEquipmentDto;
  installedBy: string;
  installedById?: string | null;
  verifiedBy: string;
  verifiedById?: string | null;
  verificationDate: string;
  notes?: string | null;
  deviationNotes?: string | null;
  /** Only honoured on create; status changes go through complete/approval. */
  status?: VerificationStatus | null;
  pdfUrl?: string | null;
  pdfGeneratedAt?: string | null;
}

export interface ApproveVerificationPayload {
  approved: boolean;
  rejectionReason?: string | null;
}

export class VerificationsApi {
  constructor(private readonly http: HttpClient) {}

  /** All of the org's verifications, newest first. */
  list(): Promise<ApiResult<VerificationDto[]>> {
    return this.http.get<VerificationDto[]>("/api/verifications");
  }

  listByProject(projectId: string): Promise<ApiResult<VerificationDto[]>> {
    return this.http.get<VerificationDto[]>("/api/verifications", {
      query: { projectId },
    });
  }

  get(id: string): Promise<ApiResult<VerificationDto>> {
    return this.http.get<VerificationDto>(`/api/verifications/${encodeURIComponent(id)}`);
  }

  /**
   * Instrument used on the org's most recent verification — pre-fills the
   * equipment block. `null` when the org has no verifications yet.
   */
  lastUsedEquipment(): Promise<ApiResult<VerificationEquipmentDto | null>> {
    return this.http.get<VerificationEquipmentDto | null>("/api/verifications/last-equipment");
  }

  create(payload: VerificationPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/verifications", { body: payload });
  }

  /** Full replace — see {@link VerificationPayload}. */
  update(id: string, payload: VerificationPayload): Promise<ApiResult<VerificationDto>> {
    return this.http.put<VerificationDto>(`/api/verifications/${encodeURIComponent(id)}`, {
      body: payload,
    });
  }

  complete(id: string): Promise<ApiResult<VerificationDto>> {
    return this.http.post<VerificationDto>(`/api/verifications/${encodeURIComponent(id)}/complete`);
  }

  approve(
    id: string,
    payload: ApproveVerificationPayload,
  ): Promise<ApiResult<VerificationDto>> {
    return this.http.post<VerificationDto>(
      `/api/verifications/${encodeURIComponent(id)}/approval`,
      { body: payload },
    );
  }

  addCircuitMeasurement(
    id: string,
    measurement: VerificationCircuitMeasurementDto,
  ): Promise<ApiResult<VerificationDto>> {
    return this.http.post<VerificationDto>(
      `/api/verifications/${encodeURIComponent(id)}/circuit-measurements`,
      { body: measurement },
    );
  }

  addRcdMeasurement(
    id: string,
    measurement: VerificationRcdMeasurementDto,
  ): Promise<ApiResult<VerificationDto>> {
    return this.http.post<VerificationDto>(
      `/api/verifications/${encodeURIComponent(id)}/rcd-measurements`,
      { body: measurement },
    );
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/verifications/${encodeURIComponent(id)}`);
  }
}
